<!--
 - Manage component (manage.component.html).
 -
 - @author Ryan Sweeney <rsweeney@buildcentrix.com>
 - @author Deitric Jang <djang@buildcentrix.com>
 - @author Jenny Lam <jlam@buildcentrix.com>
 -
 - @copyright (c) 2021-2024 Build Centrix
 -->
<div class="manage-order-form">
  <!--Header-->
  <div class="manage-order-header row mb-1">
    <div
      class="w-100"
      [hidden]="wdManageOrdersService.isLoadingOrder.value === true"
    >
      <app-orders-manage-header-component
        [orderTitle]="orderTitle"
        [orderSubtitle]="orderSubtitle"
        [order]="orderFormModel"
      >
      </app-orders-manage-header-component>
    </div>
  </div>

  <!--Content-->
  <div
    class="manage-order-content row"
    [ngClass]="{ 'loaded': wdManageOrdersService.isReloadingOrder.value === false, 'loading': wdManageOrdersService.isReloadingOrder.value === true}"
  >
    <div class="col-sm-12">
      <!--Page Loader-->
      <div
        class="bcx-loader-container"
        [hidden]="wdManageOrdersService.isLoadingOrder.value === false"
      >
        <bc-atoms-loader
          [title]="'actions.loadingType'|translate: {type:('order.title.singular'|translate)}"
          [active]="true"
          [fullscreen]="false"
        ></bc-atoms-loader>
      </div>

      <!--Order Content-->
      <div
        class="bcx-manage-order-container"
        [hidden]="(wdManageOrdersService.isLoadingOrder | async)"
      >
        <!--First Row - Order Details, Statuses, Dates, Attachments, Messages-->
        <div class="row">
          <!--Order Details-->
          <div class="details col-sm-12 col-md-12 col-lg-8 col-xl-4 mb-4">
            <div
              class="card"
              *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS]"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.details'|translate}}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isChanges || orderDetailsForm.form.invalid || !orderDetailsForm.form.dirty"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <form #orderDetailsForm="ngForm">
                  <div class="row">
                    <!--Order #-->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="workOrders"
                        labelTitle="{{'order.details.number.title'|translate}}"
                        labelDescription="{{'order.details.number.description'|translate}}"
                      >
                        <bc-atoms-input
                          id="orderNumber"
                          name="orderNumber"
                          title="{{'order.details.number.description'|translate}}"
                          placeholder="{{'order.details.number.title'|translate}}"
                          size="small"
                          [disabled]="true"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].number"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        ></bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--Tag (required based on constant isTagRequired)-->
                    <!--Note: For Lookaheads, Tag is referred to as Label -->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="workOrders"
                        labelTitle="{{ orderFormModel.isLookahead ?
                                        ('order.lookahead.label.title'|translate) : ('order.details.tag.title'|translate)
                                      }}"
                        labelDescription="{{ orderFormModel.isLookahead ?
                                        ('order.lookahead.label.description'|translate) : ('order.details.tag.description'|translate)
                                      }}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing && wdEnvironmentService.constants.isTagRequired"
                        [state]="tagControl.status"
                        [stateInput]="tagControl.errors"
                        [stateTrigger]="tagControl.invalid && (tagControl.touched)"
                      >
                        <bc-atoms-input
                          id="tag"
                          name="tag"
                          title="{{ orderFormModel.isLookahead ?
                                            ('order.lookahead.label.description'|translate) : ('order.details.tag.description'|translate)
                                          }}"
                          placeholder="{{ orderFormModel.isLookahead ?
                                            ('order.lookahead.label.title'|translate) : ('order.details.tag.title'|translate)
                                          }}"
                          size="small"
                          [required]="wdEnvironmentService.constants.isTagRequired"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].tag"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].tag"
                          #tagControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        ></bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--Ordered By-->
                    <div
                      class="col-12 col-md-4 col-lg-6"
                      *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS]"
                    >
                      <bc-atoms-input-group
                        labelIcon="user"
                        labelTitle="{{'order.details.user.orderedBy.title'|translate}}"
                        labelDescription="{{'order.details.user.orderedBy.description'|translate}}"
                      >
                        <bc-atoms-input
                          id="orderedBy"
                          name="orderedBy"
                          title="{{'order.details.user.orderedBy.description'|translate}}"
                          placeholder="{{'order.details.user.orderedBy.title'|translate}}"
                          size="small"
                          [disabled]="true"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].orderedBy.fullName"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        ></bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--Ordered For (required if permission canSubmitOrdersOnBehalf)-->
                    <div
                      class="col-12 col-md-4 col-lg-6"
                      *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS]"
                    >
                      <bc-atoms-input-group
                        labelIcon="user"
                        labelTitle="{{'order.details.user.orderedFor.title'|translate}}"
                        labelDescription="{{'order.details.user.orderedFor.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing && wdEnvironmentService.permissions.canSubmitOrdersOnBehalf"
                        [state]="orderedForControl.status"
                        [stateInput]="orderedForControl.errors"
                        [stateTrigger]="orderedForControl.invalid && (orderedForControl.touched)"
                      >
                        <bc-atoms-typeahead
                          id="orderedFor"
                          name="orderedFor"
                          title="{{'order.details.user.orderedFor.description'|translate}}"
                          placeholder="{{'order.details.user.orderedFor.title'|translate}}"
                          labelKey="fullName"
                          size="small"
                          [required]="wdEnvironmentService.permissions.canSubmitOrdersOnBehalf"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving ||
                                        wdManageOrdersService.orderDetailsData.users.value.length === 0 || !wdEnvironmentService.permissions.canSubmitOrdersOnBehalf"
                          [options]="wdManageOrdersService.orderDetailsData.users|async"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].orderedFor"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].orderedFor"
                          #orderedForControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS, orderDetailsForm.form)"
                        >
                        </bc-atoms-typeahead>
                      </bc-atoms-input-group>
                    </div>

                    <!--Measured By (required but not for lookahead orders)-->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="user"
                        labelTitle="{{'order.details.user.measuredBy.title'|translate}}"
                        labelDescription="{{'order.details.user.measuredBy.description'|translate}}"
                        [required]="!orderFormModel.isLookahead && orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing"
                        [state]="measuredByControl.status"
                        [stateInput]="measuredByControl.errors"
                        [stateTrigger]="measuredByControl.invalid && (measuredByControl.touched)"
                      >
                        <bc-atoms-input
                          id="measuredBy"
                          name="measuredBy"
                          title="{{'order.details.user.measuredBy.description'|translate}}"
                          placeholder="{{'order.details.user.measuredBy.title'|translate}}"
                          size="small"
                          [required]="!orderFormModel.isLookahead"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].measuredBy"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].measuredBy"
                          #measuredByControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        >
                        </bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--Organization-->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="workOrders"
                        labelTitle="{{'order.details.organization.title'|translate}}"
                        labelDescription="{{'order.details.organization.description'|translate}}"
                      >
                        <bc-atoms-input
                          id="organization"
                          name="organization"
                          title="{{'order.details.organization.description'|translate}}"
                          placeholder="{{'order.details.organization.title'|translate}}"
                          size="small"
                          [disabled]="true"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].organization"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        ></bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--PO Number (required based on constant isPONumberRequired)-->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="workOrders"
                        labelTitle="{{'order.details.poNumber.title'|translate}}"
                        labelDescription="{{'order.details.poNumber.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing && wdEnvironmentService.constants.isPONumberRequired"
                        [state]="poControl.status"
                        [stateInput]="poControl.errors"
                        [stateTrigger]="poControl.invalid && (poControl.touched)"
                      >
                        <bc-atoms-input
                          id="poNumber"
                          name="poNumber"
                          title="{{'order.details.poNumber.description'|translate}}"
                          placeholder="{{'order.details.poNumber.title'|translate}}"
                          size="small"
                          [required]="wdEnvironmentService.constants.isPONumberRequired"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].poNumber"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].poNumber"
                          #poControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS)"
                        ></bc-atoms-input>
                      </bc-atoms-input-group>
                    </div>

                    <!--User Group (required)-->
                    <div class="col-12 col-md-4 col-lg-6">
                      <bc-atoms-input-group
                        labelIcon="userGroup"
                        labelTitle="{{'order.details.user.group.title'|translate}}"
                        labelDescription="{{'order.details.user.group.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing"
                        [state]="userGroupControl.status"
                        [stateInput]="userGroupControl.errors"
                        [stateTrigger]="userGroupControl.invalid && (userGroupControl.touched)"
                      >
                        <bc-atoms-typeahead
                          id="userGroup"
                          name="userGroup"
                          title="{{'order.details.user.group.description'|translate}}"
                          placeholder="{{'order.details.user.group.title'|translate}}"
                          labelKey="label"
                          size="small"
                          [required]="true"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DETAILS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DETAILS].isSaving ||
                                                wdManageOrdersService.orderDetailsData.userGroups.value.length === 0"
                          [options]="wdManageOrdersService.orderDetailsData.userGroups|async"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].userGroup"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].userGroup"
                          #userGroupControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DETAILS, orderDetailsForm.form)"
                        >
                        </bc-atoms-typeahead>
                      </bc-atoms-input-group>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <!--Statuses-->
          <div class="statuses col-sm-12 col-md-12 col-lg-4 col-xl-2 mb-4">
            <div
              class="card"
              *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.STATUSES]"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.status'|translate}}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails ||
                                                       wdEnvironmentService.permissions.canChangeOrderStatus ||
                                                       wdEnvironmentService.permissions.canChangeDeliveryStatus ||
                                                       wdEnvironmentService.permissions.canChangeProductionStatus"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.STATUSES)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.STATUSES)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isChanges"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.STATUSES)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row">
                  <!--Overall Status-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="all"
                      labelTitle="{{'order.status.overall.title'|translate}}"
                      labelDescription="{{'order.status.overall.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                    >
                      <bc-atoms-dropdown
                        id="overallStatus"
                        name="overallStatus"
                        title="{{'order.status.overall.description'|translate}}"
                        placeholder="{{'order.status.overall.title'|translate}}"
                        labelKey="label"
                        display="block"
                        iconKey="type"
                        size="small"
                        [requireSelection]="true"
                        [loading]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving || !wdEnvironmentService.permissions.canChangeOrderStatus"
                        [options]="wdOrderStatusService.overallStatusOptions | async"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.STATUSES].overall"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.STATUSES)"
                      >
                      </bc-atoms-dropdown>
                    </bc-atoms-input-group>
                  </div>
                  <!--Production Status-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="all"
                      labelTitle="{{'order.status.production.title'|translate}}"
                      labelDescription="{{'order.status.production.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                    >
                      <bc-atoms-dropdown
                        title="{{'order.status.production.description'|translate}}"
                        display="block"
                        labelKey="label"
                        iconKey="type"
                        size="small"
                        [requireSelection]="true"
                        [loading]="wdOrderStatusService.isLoading | async"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving || !wdEnvironmentService.permissions.canChangeProductionStatus"
                        [options]="wdOrderStatusService.productionStatusOptions | async"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.STATUSES].production"
                      >
                      </bc-atoms-dropdown>
                    </bc-atoms-input-group>
                  </div>
                  <!--Shipping Status-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="all"
                      labelTitle="{{'order.status.shipping.title'|translate}}"
                      labelDescription="{{'order.status.shipping.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing"
                    >
                      <bc-atoms-dropdown
                        title="{{'order.status.shipping.description'|translate}}"
                        display="block"
                        labelKey="label"
                        iconKey="type"
                        size="small"
                        [requireSelection]="true"
                        [loading]="orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.STATUSES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.STATUSES].isSaving || !wdEnvironmentService.permissions.canChangeDeliveryStatus"
                        [options]="wdOrderStatusService.shippingStatusOptions | async"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.STATUSES].shipping"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.STATUSES)"
                      >
                      </bc-atoms-dropdown>
                    </bc-atoms-input-group>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--Dates-->
          <div class="dates col-sm-12 col-md-12 col-lg-4 col-xl-2 mb-4">
            <div class="card" *ngIf="orderFormModel.dates">
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.dates'|translate}}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.DATES)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.DATES)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.DATES].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DATES].isChanges || !isProductionDateRangeValid || !isProductionEndBeforeShipping"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.DATES)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row">
                  <!--Ordered-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="calendar"
                      labelTitle="{{'order.dates.ordered.title'|translate}}"
                      labelDescription="{{'order.dates.ordered.description'|translate}}"
                    >
                      <bc-atoms-date-picker
                        id="dateOrdered"
                        name="dateOrdered"
                        placeholder="{{'order.dates.ordered.title'|translate}}"
                        title="{{'order.dates.ordered.description'|translate}}"
                        size="small"
                        [disabled]="true"
                        [dateFormat]="wdEnvironmentService.dateFormat"
                        [model]="(orderFormModel.dates)?.ordered"
                      >
                      </bc-atoms-date-picker>
                    </bc-atoms-input-group>
                  </div>

                  <!--Requested-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="calendar"
                      labelTitle="{{'order.dates.requested.title'|translate}}"
                      labelDescription="{{'order.dates.requested.description'|translate}}"
                    >
                      <bc-atoms-date-picker
                        id="dateRequested"
                        name="dateRequested"
                        placeholder="{{'order.dates.requested.title'|translate}}"
                        title="{{'order.dates.requested.description'|translate}}"
                        size="small"
                        [disabled]="true"
                        [dateFormat]="wdEnvironmentService.dateFormat"
                        [model]="(orderFormModel.dates)?.requested"
                      >
                      </bc-atoms-date-picker>
                    </bc-atoms-input-group>
                  </div>

                  <!--Production start date-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="calendar"
                      labelTitle="{{'order.dates.productionStart.title'|translate}}"
                      labelDescription="{{'order.dates.productionStart.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                    >
                      <bc-atoms-date-picker
                        id="dateProductionStart"
                        name="dateProductionStart"
                        placeholder="{{'order.dates.productionStart.title'|translate}}"
                        title="{{'order.dates.productionStart.description'|translate}}"
                        size="small"
                        [min]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].ordered"
                        [max]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].productionEnd"
                        [required]="true"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DATES].isSaving || !wdEnvironmentService.permissions.canChangeProductionDate"
                        [dateFormat]="wdEnvironmentService.dateFormat"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].productionStart"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DATES)"
                      >
                      </bc-atoms-date-picker>
                    </bc-atoms-input-group>
                    <div
                      class="date-range-message"
                      *ngIf="!isProductionDateRangeValid"
                    >
                      {{'order.dates.productionDateRange.invalidDateRange'|translate}}
                    </div>
                  </div>

                  <!--Production end date-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="calendar"
                      labelTitle="{{'order.dates.productionEnd.title'|translate}}"
                      labelDescription="{{'order.dates.productionEnd.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                    >
                      <bc-atoms-date-picker
                        id="dateProductionEnd"
                        name="dateProductionEnd"
                        placeholder="{{'order.dates.productionEnd.title'|translate}}"
                        title="{{'order.dates.productionEnd.description'|translate}}"
                        size="small"
                        [min]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].productionStart"
                        [max]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].delivery"
                        [required]="true"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DATES].isSaving || !wdEnvironmentService.permissions.canChangeProductionDate"
                        [dateFormat]="wdEnvironmentService.dateFormat"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].productionEnd"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DATES)"
                      >
                      </bc-atoms-date-picker>
                    </bc-atoms-input-group>
                    <div
                      class="date-range-message"
                      *ngIf="!isProductionDateRangeValid"
                    >
                      {{'order.dates.productionDateRange.invalidDateRange'|translate}}
                    </div>
                    <div
                      class="date-range-message"
                      *ngIf="!isProductionEndBeforeShipping"
                    >
                      {{'order.dates.productionDateRange.conflictsWithShippingDate'|translate}}
                    </div>
                  </div>

                  <!--Shipping-->
                  <div class="col-md-6 col-lg-12">
                    <bc-atoms-input-group
                      labelIcon="calendar"
                      labelTitle="{{'order.dates.shipping.title'|translate}}"
                      labelDescription="{{'order.dates.shipping.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing"
                    >
                      <bc-atoms-date-picker
                        id="dateShipping"
                        name="dateShipping"
                        placeholder="{{'order.dates.shipping.title'|translate}}"
                        title="{{'order.dates.shipping.description'|translate}}"
                        size="small"
                        [min]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].productionEnd"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.DATES]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.DATES].isSaving || !wdEnvironmentService.permissions.canChangeDeliveryDate"
                        [dateFormat]="wdEnvironmentService.dateFormat"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.DATES].delivery"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.DATES)"
                      >
                      </bc-atoms-date-picker>
                    </bc-atoms-input-group>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--Attachments-->
          <div class="attachments col-sm-12 col-md-6 col-lg-4 col-xl-2 mb-4">
            <div
              class="card"
              *ngIf="!wdManageOrdersService.isLoadingOrder.value === true"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.attachments'|translate: { count:
                  orderFormModel.attachments?.length } }}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                >
                  <!--Add-->
                  <div class="d-inline-block">
                    <bc-atoms-button
                      title="{{'actions.add'|translate }}"
                      icon="add"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.ATTACHMENTS]?.isLoading"
                      [iconOnly]="true"
                      onclick="document.getElementById('fileMultiple').click();"
                    >
                    </bc-atoms-button>
                    <input
                      id="fileMultiple"
                      type="file"
                      multiple
                      [hidden]="true"
                      (change)="onFilesSelected($event)"
                    />
                  </div>
                </div>
              </div>
              <div class="card-body order-attachments-container">
                <!--Saved Attachments-->
                <app-order-attachments
                  [orderIndex]="orderIndex"
                  [attachments]="orderFormModel.attachments"
                  (isChanges)="loadOrder(false)"
                >
                </app-order-attachments>
              </div>
            </div>
          </div>
          <!--Messages-->
          <div class="messages col-sm-12 col-md-6 col-lg-4 col-xl-2 mb-4">
            <div
              class="card"
              *ngIf="!wdManageOrdersService.isLoadingOrder.value === true"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.messages'|translate: { count:
                  orderFormModel[wdLocalSharedFunctions.orderSectionType.MESSAGES]?.valuesDisplayed?.length
                  } }}
                </div>
                <div class="header-buttons">
                  <!--Settings-->
                  <div class="d-inline-block">
                    <bc-atoms-button
                      title="{{'actions.add'|translate }}"
                      icon="all"
                      display="block"
                      size="small"
                      [loading]="false"
                      [iconOnly]="true"
                    >
                      <div dropdown-content>
                        <form class="ml-2 mr-2">
                          <!--Filters-->
                          <bc-atoms-input-group
                            labelIcon="filter"
                            labelTitle="{{'order.messages.filters.title'|translate}}"
                            labelDescription="{{'order.messages.filters.description'|translate}}"
                          >
                            <div class="dropdown-item">
                              <bc-atoms-checkbox
                                [label]="'order.messages.transaction.title'|translate"
                                [title]="'order.messages.transaction.description'|translate"
                                [(model)]="wdOrderMessageService.showTransactionMessages"
                                (onCheckboxClicked)="filterMessages()"
                              >
                              </bc-atoms-checkbox>
                            </div>
                            <div class="dropdown-item">
                              <bc-atoms-checkbox
                                [label]="'order.messages.user.title'|translate"
                                [title]="'order.messages.user.description'|translate"
                                [(model)]="wdOrderMessageService.showUserMessages"
                                (onCheckboxClicked)="filterMessages()"
                              >
                              </bc-atoms-checkbox>
                            </div>
                          </bc-atoms-input-group>
                          <!--Options-->
                          <bc-atoms-input-group
                            labelIcon="all"
                            labelTitle="{{'order.messages.options.title'|translate}}"
                            labelDescription="{{'order.messages.options.description'|translate}}"
                            *ngIf="wdEnvironmentService.permissions.canAddOrderMessages"
                          >
                            <div class="dropdown-item">
                              <bc-atoms-checkbox
                                [label]="'order.messages.notify.title'|translate"
                                [title]="'order.messages.notify.description'|translate"
                                [(model)]="wdOrderMessageService.sendNotifications"
                              >
                              </bc-atoms-checkbox>
                            </div>
                          </bc-atoms-input-group>
                        </form>
                      </div>
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body order-messages-container">
                <!--Messages-->
                <app-order-messages
                  [orderIndex]="orderIndex"
                  [messages]="orderFormModel[wdLocalSharedFunctions.orderSectionType.MESSAGES]?.valuesDisplayed"
                  (messagesChanged)="onMessagesChanges()"
                ></app-order-messages>
              </div>
            </div>
          </div>
        </div>

        <!--Second Row - Job Details, Shipping Details, Special Instructions-->
        <div class="row">
          <!--Job Details-->
          <div class="job col-sm-12 col-md-12 col-lg-8 col-xl-4 mb-4">
            <div
              class="card"
              *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS]"
              [ngClass]="{ 'ecommerce': hasEcomJobOverride() }"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.job'|translate}}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="(!orderFormModel.isEcommerce && wdEnvironmentService.permissions.canEditOrderDetails) ||
                             (orderFormModel.isEcommerce && (wdEnvironmentService.permissions.canEditEcommerceJob && wdEnvironmentService.permissions.canEditOrderDetails))"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.JOB);"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.JOB);"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB].isChanges || jobForm.form.invalid || !jobForm.form.dirty"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.JOB)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <form #jobForm="ngForm">
                  <div class="row">
                    <!--Job Name/Number (Combined)  -->
                    <div
                      class="col-12 col-md-8 col-lg-8"
                      *ngIf="!orderFormModel.isEcommerce"
                    >
                      <bc-atoms-input-group
                        labelIcon="jobsAdmin"
                        labelTitle="{{'order.job.title'|translate}}"
                        labelDescription="{{'order.job.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                        [state]="jobControl.status"
                        [stateInput]="jobControl.errors"
                        [stateTrigger]="jobControl.invalid && (jobControl.touched)"
                        [loading]="wdJobService.searchingJobs"
                        loadingText="{{'labels.loadingResults'|translate}}"
                      >
                        <!--Search Typeahead-->
                        <label for="jobCombined" [hidden]="true"></label>
                        <input
                          id="jobCombined"
                          name="jobCombined"
                          title="{{'order.job.description'|translate}}"
                          type="text"
                          class="form-control form-control-sm"
                          [class.is-invalid]="wdJobService.searchingJobsFailed"
                          [editable]="false"
                          [resultTemplate]="rt"
                          [inputFormatter]="wdJobService.typeaheadSearchJobsFormatter"
                          [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving"
                          [ngModelOptions]="{standalone: true}"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job"
                          (ngModelChange)="jobChange($event, jobForm.form)"
                          [ngbTypeahead]="wdJobService.typeaheadSearchJobs"
                          (blur)="typeaheadJobBlur(orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job)"
                          #jobControl="ngModel"
                        />

                        <!--Typeahead Result Template-->
                        <ng-template #rt let-r="result" let-t="term">
                          <ngb-highlight
                            [result]="r.number + ' ' + r.name"
                            [term]="t"
                          ></ngb-highlight>
                        </ng-template>
                      </bc-atoms-input-group>
                    </div>

                    <!--Job Number (Separate)-->
                    <div
                      class="col-12 col-md-4 col-lg-4"
                      *ngIf="orderFormModel.isEcommerce"
                    >
                      <!--Original Job Number-->
                      <bc-atoms-input-group
                        labelIcon="jobsAdmin"
                        labelTitle="{{'order.job.number.title'|translate}}"
                        labelDescription="{{'order.job.number.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                        [state]="jobNumberControl.status"
                        [stateInput]="jobNumberControl.errors"
                        [stateTrigger]="jobNumberControl.invalid && (jobNumberControl.touched)"
                        [loading]="wdJobService.searchingJobs"
                        loadingText="{{'labels.loadingResults'|translate}}"
                      >
                        <bc-atoms-typeahead
                          id="jobNumber"
                          name="jobNumber"
                          title="{{'order.job.number.description'|translate}}"
                          placeholder="{{'order.job.number.title'|translate}}"
                          size="small"
                          labelKey="number"
                          [editable]="true"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving || orderFormModel.isEcommerce"
                          [options]="jobTypeaheadResults|async"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job"
                          #jobNumberControl="ngModel"
                        >
                        </bc-atoms-typeahead>
                      </bc-atoms-input-group>

                      <!--Override Job Number-->
                      <div *ngIf="hasEcomJobOverride()">
                        <bc-atoms-input-group
                          labelIcon="adjustment"
                          labelTitle="{{'order.job.override.number.title'|translate}}"
                          labelDescription="{{'order.job.override.number.description'|translate}}"
                          [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                          [state]="jobNumberOverrideControl.status"
                          [stateInput]="jobNumberOverrideControl.errors"
                          [stateTrigger]="jobNumberOverrideControl.invalid && (jobNumberOverrideControl.touched)"
                          *ngIf="orderFormModel.isEcommerce"
                        >
                          <bc-atoms-input
                            id="jobNumberOverride"
                            name="jobNumberOverride"
                            title="{{'order.job.override.number.description'|translate}}"
                            placeholder="{{'order.job.override.number.title'|translate}}"
                            size="small"
                            [required]="orderFormModel.isEcommerce"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving"
                            [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job.override.number"
                            [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job.override.number"
                            #jobNumberOverrideControl="ngModel"
                            (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.JOB, jobForm.form)"
                          >
                          </bc-atoms-input>
                        </bc-atoms-input-group>
                      </div>
                    </div>

                    <!--Job Name (Separate)-->
                    <div
                      class="col-12 col-md-4 col-lg-4"
                      [ngClass]="{ 'col-md-8 col-lg-8': orderFormModel.isEcommerce }"
                      *ngIf="orderFormModel.isEcommerce"
                    >
                      <!--Original Job Name-->
                      <bc-atoms-input-group
                        labelIcon="jobsAdmin"
                        labelTitle="{{'order.job.name.title'|translate}}"
                        labelDescription="{{'order.job.name.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                        [state]="jobNameControl.status"
                        [stateInput]="jobNameControl.errors"
                        [stateTrigger]="jobNameControl.invalid && (jobNameControl.touched)"
                        [loading]="wdJobService.searchingJobs"
                        loadingText="{{'labels.loadingResults'|translate}}"
                      >
                        <bc-atoms-typeahead
                          id="jobName"
                          name="jobName"
                          title="{{'order.job.name.description'|translate}}"
                          placeholder="{{'order.job.name.title'|translate}}"
                          size="small"
                          labelKey="name"
                          [editable]="true"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving || orderFormModel.isEcommerce"
                          [options]="jobTypeaheadResults|async"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job"
                          #jobNameControl="ngModel"
                        >
                        </bc-atoms-typeahead>
                      </bc-atoms-input-group>

                      <div *ngIf="hasEcomJobOverride()">
                        <!--Override Job Name-->
                        <bc-atoms-input-group
                          labelIcon="adjustment"
                          labelTitle="{{'order.job.override.name.title'|translate}}"
                          labelDescription="{{'order.job.override.name.description'|translate}}"
                          [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing"
                          [state]="jobNameOverrideControl.status"
                          [stateInput]="jobNameOverrideControl.errors"
                          [stateTrigger]="jobNameOverrideControl.invalid && (jobNameOverrideControl.touched)"
                          *ngIf="orderFormModel.isEcommerce"
                        >
                          <bc-atoms-input
                            id="jobNameOverride"
                            name="jobNameOverride"
                            title="{{'order.job.override.name.description'|translate}}"
                            placeholder="{{'order.job.override.name.title'|translate}}"
                            size="small"
                            [required]="orderFormModel.isEcommerce"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB].isSaving"
                            [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job.override.name"
                            [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].job.override.name"
                            #jobNameOverrideControl="ngModel"
                            (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.JOB, jobForm.form)"
                          >
                          </bc-atoms-input>
                        </bc-atoms-input-group>
                      </div>
                    </div>
                    <!--Job Phase-->
                    <div
                      class="col-12 col-md-4 col-lg-4"
                      *ngIf="!orderFormModel.isEcommerce"
                    >
                      <bc-atoms-input-group
                        labelIcon="jobsAdmin"
                        labelTitle="{{'order.job.phase.title'|translate}}"
                        labelDescription="{{'order.job.phase.description'|translate}}"
                        [required]="orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing && phaseTypeaheadResults.value.length > 0"
                        [state]="jobPhaseControl.status"
                        [stateInput]="jobPhaseControl.errors"
                        [stateTrigger]="jobPhaseControl.invalid && (jobPhaseControl.touched)"
                      >
                        <bc-atoms-typeahead
                          id="jobPhase"
                          name="jobPhase"
                          title="{{'order.job.phase.description'|translate}}"
                          placeholder="{{'order.job.phase.title'|translate}}"
                          labelKey="labelWithCode"
                          size="small"
                          [required]="phaseTypeaheadResults.value.length > 0"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.JOB]?.isSaving || phaseTypeaheadResults.value.length === 0"
                          [options]="phaseTypeaheadResults|async"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].phase"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.JOB].phase"
                          #jobPhaseControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.JOB, jobForm.form)"
                        >
                        </bc-atoms-typeahead>
                      </bc-atoms-input-group>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <!--Shipping-->
          <div class="shipping col-sm-12 col-md-12 col-lg-12 col-xl-4 mb-4">
            <div
              class="card"
              *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING]"
            >
              <div class="card-header">
                <div class="header-title">
                  {{'order.details.sections.shipping'|translate}}
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING); setShowShippingDetailsContainerFlag(false);"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isChanges || !this.isDeliveryWindowValid"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row">
                  <!--Shipping Option-->
                  <div class="shipping-option col-12 col-md-4 col-lg-6">
                    <bc-atoms-input-group
                      labelIcon="shipped"
                      labelTitle="{{'order.shipping.option.title'|translate}}"
                      labelDescription="{{'order.shipping.option.description'|translate}}"
                      [required]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                      *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING]"
                    >
                      <bc-atoms-dropdown
                        title="{{'order.shipping.option.title'|translate}}"
                        display="block"
                        labelKey="label"
                        iconKey="type"
                        size="small"
                        [requireSelection]="true"
                        [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || !wdEnvironmentService.permissions.canEditOrderDetails"
                        [options]="wdOrderShippingOptionsService.shippingOptionsActive | async"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].option"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                      >
                      </bc-atoms-dropdown>
                      <div
                        class="address-link"
                        *ngIf="showShippingDetailsContainer"
                      >
                        <a
                          href="javascript:void(0);"
                          title="{{'order.shipping.option.address.view.description'|translate}}"
                          (click)="this.viewportScroller.scrollToAnchor('shipping-address');"
                        >
                          {{'order.shipping.option.address.view.title'|translate}}
                        </a>
                      </div>
                    </bc-atoms-input-group>
                  </div>
                  <!--Shipping Loading Zone-->
                  <div class="col-12 col-md-4 col-lg-6">
                    <bc-atoms-input-group
                      labelIcon="shippingLoadingZone"
                      labelTitle="{{'order.shipping.loadingZone.title'|translate}}"
                      labelDescription="{{'order.shipping.loadingZone.description'|translate}}"
                      [required]="false"
                      *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING]"
                    >
                      <bc-atoms-dropdown
                        title="{{'order.shipping.loadingZone.title'|translate}}"
                        display="block"
                        labelKey="label"
                        label="{{'order.shipping.loadingZone.default'|translate}}"
                        iconKey="type"
                        size="small"
                        [requireSelection]="false"
                        [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                        [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || !wdEnvironmentService.permissions.canEditOrderDetails"
                        [options]="wdOrderShippingLoadingZonesService.loadingZoneOptions | async"
                        [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].loadingZone"
                        (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                      >
                      </bc-atoms-dropdown>
                    </bc-atoms-input-group>
                  </div>
                  <!--Shipping Vehicle Assignments-->
                  <div class="col-12 col-md-4 col-lg-6">
                    <bc-atoms-input-group
                      labelIcon="shippingVehicle"
                      labelTitle="{{'order.shipping.vehicle.assignment.title'|translate}}"
                      labelDescription="{{'order.shipping.vehicle.assignment.description'|translate}}"
                      [required]="false"
                      *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING] && wdEnvironmentService.permissions.canViewVehicleAssignments"
                    >
                      <!--No Vehicle Assignment Options-->
                      <div
                        *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignmentOptions?.length === 0"
                      >
                        <bc-atoms-button
                          label="{{'order.columns.shipping.assignments.add.title'|translate}}"
                          title="{{'order.columns.shipping.assignments.add.description'|translate}}"
                          icon="add"
                          display="block"
                          size="small"
                          type="default"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                          (onButtonClicked)="addVehicleAssignmentForOrder(orderFormModel)"
                        >
                        </bc-atoms-button>
                      </div>

                      <!--Vehicle Assignment Options-->
                      <div
                        (click)="openVehicleAssignmentsModal(orderFormModel)"
                        *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignmentOptions?.length > 0"
                      >
                        <!--Vehicle Assignment Options and None Selected-->
                        <div
                          *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments?.length === 0"
                        >
                          <bc-atoms-button
                            label="{{'order.columns.shipping.assignments.none.title'|translate}}"
                            title="{{'order.columns.shipping.assignments.none.description'|translate}}"
                            icon="edit"
                            display="block"
                            size="small"
                            type="default"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                          >
                          </bc-atoms-button>
                        </div>

                        <!--Vehicle Assignment Options and One Selected-->
                        <div
                          *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments?.length === 1"
                        >
                          <bc-atoms-button
                            label="{{orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments[0].labelShort}}"
                            title="{{orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments[0].labelShort}}"
                            icon="edit"
                            display="block"
                            size="small"
                            type="default"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                          >
                          </bc-atoms-button>
                        </div>

                        <!--Vehicle Assignment Options and Multiple Selected-->
                        <div
                          *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments?.length > 1"
                        >
                          <bc-atoms-button
                            label="{{orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments.length}} {{'order.columns.shipping.assignments.title'|translate}}"
                            title="{{orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].vehicle?.assignments.length}} {{'order.columns.shipping.assignments.title'|translate}}"
                            icon="edit"
                            display="block"
                            size="small"
                            type="default"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                          >
                          </bc-atoms-button>
                        </div>
                      </div>
                    </bc-atoms-input-group>
                  </div>
                  <!--Shipping Time Window-->
                  <div class="col-12 col-md-4 col-lg-6 time-window-container">
                    <bc-atoms-input-group
                      labelIcon="time"
                      labelTitle="{{'order.shipping.time.window.title'|translate}}"
                      labelDescription="{{'order.shipping.time.window.description'|translate}}"
                    >
                      <div class="row">
                        <div class="col-6 time-window">
                          <bc-atoms-input-group
                            [required]="false"
                            [state]="startTimeControl.status"
                            [stateInput]="startTimeControl.errors"
                            [stateTrigger]="startTimeControl.invalid && (startTimeControl.touched)"
                          >
                            <ngb-timepicker
                              [spinners]="false"
                              [hidden]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                              size="small"
                              [meridian]="true"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || !wdEnvironmentService.permissions.canEditOrderDetails"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].startTime.objectValue"
                              [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].startTime.objectValue"
                              (ngModelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                              #startTimeControl="ngModel"
                            ></ngb-timepicker>
                            <bc-atoms-input
                              id="startTime"
                              name="startTime"
                              title="{{'order.shipping.time.start.title'|translate}}"
                              placeholder="{{'order.shipping.time.start.title'|translate}}"
                              [hidden]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                              size="small"
                              [disabled]="true"
                              [model]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].startTime.readValue"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>
                        <div class="col-6 time-window">
                          <bc-atoms-input-group
                            [required]="false"
                            [state]="endTimeControl.status"
                            [stateInput]="endTimeControl.errors"
                            [stateTrigger]="endTimeControl.invalid && (endTimeControl.touched)"
                          >
                            <ngb-timepicker
                              [spinners]="false"
                              [hidden]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                              size="small"
                              [meridian]="true"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || !wdEnvironmentService.permissions.canEditOrderDetails"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].endTime.objectValue"
                              [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].endTime.objectValue"
                              (ngModelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                              #endTimeControl="ngModel"
                            >
                            </ngb-timepicker>
                            <bc-atoms-input
                              id="endTime"
                              name="endTime"
                              title="{{'order.shipping.time.end.title'|translate}}"
                              placeholder="{{'order.shipping.time.end.title'|translate}}"
                              [hidden]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                              size="small"
                              [disabled]="true"
                              [model]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].endTime.readValue"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>
                        <div
                          class="col-12 date-range-message"
                          *ngIf="!isDeliveryWindowValid"
                        >
                          {{'order.shipping.time.deliveryWindowRange.invalidDateRange'|translate}}
                        </div>
                      </div>
                    </bc-atoms-input-group>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--Special Instructions - NOTE: For Lookaheads this is referred to as Description-->
          <div class="instructions col-sm-12 col-md-12 col-lg-12 col-xl-4 mb-4">
            <div
              class="card"
              *ngIf="orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS]"
              [ngClass]="{ 'ecommerce': orderFormModel.isEcommerce }"
            >
              <div class="card-header">
                <div class="header-title">
                  <div class="header-title">
                    <span *ngIf="!orderFormModel.isLookahead"
                      >{{'order.details.sections.instructions'|translate}}</span
                    >
                    <span *ngIf="orderFormModel.isLookahead"
                      >{{'order.lookahead.description.title'|translate}}</span
                    >
                  </div>
                </div>
                <div
                  class="header-buttons"
                  *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                >
                  <!--Edit-->
                  <div
                    class="d-inline-block"
                    *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.edit'|translate }}"
                      icon="edit"
                      display="block"
                      size="small"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]?.isLoading"
                      [iconOnly]="true"
                      (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Cancel-->
                  <div
                    class="d-inline-block"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.cancel'|translate }}"
                      icon="cancel"
                      display="block"
                      size="small"
                      type="default"
                      [iconOnly]="true"
                      (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS)"
                    >
                    </bc-atoms-button>
                  </div>

                  <!--Save-->
                  <div
                    class="d-inline-block ml-2"
                    *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]?.isEditing"
                  >
                    <bc-atoms-button
                      title="{{'actions.save'|translate }}"
                      icon="save"
                      display="block"
                      size="small"
                      type="success"
                      [loading]="orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS].isSaving"
                      [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS].isChanges || specialInstructionsForm.form.invalid || !specialInstructionsForm.form.dirty"
                      [iconOnly]="true"
                      (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS)"
                    >
                    </bc-atoms-button>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <form #specialInstructionsForm="ngForm">
                  <div class="row">
                    <!--Special Instructions (not required)-->
                    <div class="col-12">
                      <bc-atoms-input-group
                        labelIcon="notifications"
                        labelTitle="{{ orderFormModel.isLookahead ?
                                ('order.lookahead.description.title'|translate) : ('order.instructions.title'|translate)
                            }}"
                        labelDescription="{{ orderFormModel.isLookahead ?
                                ('order.lookahead.description.description'|translate) : ('order.instructions.description'|translate)
                            }}"
                        [state]="specialInstructionsControl.status"
                        [stateInput]="specialInstructionsControl.errors"
                        [stateTrigger]="specialInstructionsControl.invalid && (specialInstructionsControl.touched)"
                      >
                        <bc-atoms-textarea
                          id="specialInstructions"
                          name="specialInstructions"
                          placeholder="{{ orderFormModel.isLookahead ?
                                    ('order.lookahead.description.title'|translate) : ('order.instructions.title'|translate)
                                }}"
                          title="{{ orderFormModel.isLookahead ?
                                ('order.lookahead.description.description'|translate) : ('order.instructions.description'|translate)
                            }}"
                          size="small"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS].isSaving"
                          [rows]="5"
                          [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]"
                          [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS]"
                          #specialInstructionsControl="ngModel"
                          (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS)"
                        ></bc-atoms-textarea>
                      </bc-atoms-input-group>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <!--Add Adjustment-->
        <div
          class="row"
          *ngIf="wdEnvironmentService.permissions.canManageAdjustments || orderFormModel.fulfillment?.groups.length > 0"
        >
          <div class="col-12">
            <div class="row">
              <!--Adjustment Card-->
              <div class="fg-adjustments-title col-12 mb-4">
                <div class="card">
                  <div class="card-header">
                    <div class="header-title">
                      {{'order.titles.groupsAndAdjustments'|translate }}
                    </div>
                    <div
                      class="header-buttons"
                      *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                    >
                      <!--Add Adjustment-->
                      <div class="d-inline-block">
                        <bc-atoms-button
                          title="{{'actions.addType'|translate: { type:('order.adjustment.singular.description'|translate) } }}"
                          label="{{'order.adjustment.singular.title'|translate}}"
                          icon="add"
                          display="block"
                          size="small"
                          type="default"
                          [loading]="wdManageOrdersService.isLoadingOrder.value === true"
                          (onButtonClicked)="manageAdjustment()"
                        >
                        </bc-atoms-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!--Fulfillment Groups-->
        <div class="row">
          <div
            class="col-12"
            *ngFor="let group of orderFormModel.fulfillment?.groups"
          >
            <div class="row">
              <!--Fulfillment Group Card-->
              <div
                class="fulfillment-group col-12 mb-4"
                *ngIf="activeFulfillmentGroupIndexes.includes(group.index)"
              >
                <div class="card">
                  <div class="card-header">
                    <div class="header-title">
                      {{group.label}} [{{group.code}}]
                    </div>
                    <div class="header-buttons">
                      <!--Add Adjustment-->
                      <div
                        class="d-inline-block"
                        *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                      >
                        <bc-atoms-button
                          title="{{'actions.addType'|translate: { type:('order.adjustment.singular.description'|translate) } }}"
                          label="{{'order.adjustment.singular.title'|translate}}"
                          icon="add"
                          display="block"
                          size="small"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.ATTACHMENTS]?.isLoading"
                          (onButtonClicked)="manageAdjustment(group)"
                        >
                        </bc-atoms-button>
                      </div>
                      <!--Hole data report-->
                      <div
                        class="d-inline-block ml-2"
                        *ngIf="wdOrderItemHolesService.checkIfFulfillmentGroupHasHoleData(group)"
                      >
                        <bc-atoms-button
                          title="{{'order.holeData.description'|translate}}"
                          label="{{'order.holeData.title'|translate}}"
                          icon="download"
                          display="block"
                          size="small"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.ATTACHMENTS]?.isLoading"
                          (onButtonClicked)="exportHoleData(group)"
                        >
                        </bc-atoms-button>
                      </div>
                      <!--Fulfillment Group Reports (PDF, CSV)-->
                      <div
                        class="d-inline-block ml-2"
                        *ngIf="(group.fittingsDataSource.data.length > 0 || group.productsDataSource.data.length > 0) &&
                                   (wdEnvironmentService.permissions.canViewFulfillmentCsvExport || wdEnvironmentService.permissions.canViewFulfillmentPdfExport) &&
                                   group.module?.id !== 'tracker'"
                      >
                        <bc-atoms-button
                          id="btnFgExports"
                          title="{{'exports.fulfillment.groups.reports.title'|translate }}"
                          icon="export"
                          display="block"
                          size="small"
                          [disabled]="false"
                          [iconOnly]="true"
                        >
                          <div dropdown-content>
                            <form class="m-2">
                              <!--Reports-->
                              <bc-atoms-input-group
                                labelIcon="all"
                                labelTitle="{{'exports.fulfillment.groups.reports.title'|translate}}"
                                labelDescription="{{'exports.fulfillment.groups.reports.description'|translate}}"
                              >
                                <div
                                  class="dropdown-item"
                                  *ngFor="let exportElem of group.exports"
                                >
                                  <a
                                    [href]="exportElem.url"
                                    [target]="exportElem.target"
                                  >
                                    <bc-atoms-icon
                                      [type]="exportElem.icon"
                                    ></bc-atoms-icon>
                                    <label>{{exportElem.label}}</label>
                                  </a>
                                </div>
                              </bc-atoms-input-group>
                            </form>
                          </div>
                        </bc-atoms-button>
                      </div>
                      <!--Fulfillment Group Status-->
                      <div
                        class="d-inline-block ml-2"
                        *ngIf="group.status && group.module?.id !== 'tracker'"
                      >
                        <bc-atoms-dropdown
                          id="btnFgStatus"
                          labelKey="label"
                          display="block"
                          size="small"
                          title="{{'order.fulfillment.groups.status.description'|translate}}"
                          [requireSelection]="true"
                          [loading]="group.isUpdatingOrder"
                          [disabled]="!wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                          [options]="wdOrderStatusService.productionStatusOptions | async"
                          [(model)]="group.status"
                          (modelChange)="updateFulfillmentGroupStatus(orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].index, group)"
                        >
                        </bc-atoms-dropdown>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <!--Fittings-->
                    <div *ngIf="group.fittingsDataSource.data.length > 0">
                      <div class="fg-header">
                        <!--Title-->
                        <bc-atoms-input-group
                          labelTitle="{{group.label}} {{'order.fulfillment.groups.fittings.title'|translate}}"
                          labelDescription="{{group.label}} {{'order.fulfillment.groups.fittings.description'|translate}}"
                        >
                        </bc-atoms-input-group>

                        <!--Buttons-->
                        <div class="header-buttons">
                          <!--Status Update-->
                          <div
                            class="d-inline-block"
                            *ngIf="group.fittingsSelection.selected.length > 0"
                          >
                            <bc-atoms-dropdown
                              labelKey="label"
                              size="small"
                              title="{{'order.fulfillment.groups.fittings.status.description'|translate}}"
                              label="{{'order.fulfillment.groups.fittings.status.default.title'|translate}}"
                              type="success"
                              [requireSelection]="false"
                              [loading]="group.fittingsUpdating"
                              [disabled]="group.fittingsUpdating"
                              [options]="wdOrderStatusService.orderItemShippingStatusOptions | async"
                              [model]="group.fittingStatusesDropdownValue"
                              (modelChange)="updateSelectedOrderItemShippingStatuses(orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].id, group, $event, 'fittings')"
                            >
                            </bc-atoms-dropdown>
                          </div>
                        </div>
                      </div>
                      <div class="row">
                        <!--Fittings Table-->
                        <div class="col-12 items-table">
                          <div class="no-more-tables">
                            <bc-templates-table-container
                              class="order-items-table"
                              [dataSource]="group.fittingsDataSource"
                              [headers]="orderFittingColumns"
                              [showPagination]="false"
                              [isLoading]="isLoading"
                              [customColSpan]="orderFittingColumns.length"
                              [paginationPageSize]="group.fittingsDataSource?.data?.length"
                              matSort
                            >
                              <!-- Checkbox Column -->
                              <ng-container matColumnDef="select">
                                <th
                                  mat-header-cell
                                  class="select-column"
                                  *matHeaderCellDef
                                >
                                  <!--Select All Checkbox-->
                                  <mat-checkbox
                                    (change)="$event ? masterToggle(group.fittingsDataSource, group.fittingsSelection) : null"
                                    disableRipple="true"
                                    [checked]="group.fittingsSelection.hasValue() &&
                                                                isAllSelected(group.fittingsDataSource, group.fittingsSelection)"
                                    [indeterminate]="group.fittingsSelection.hasValue() &&
                                                                !isAllSelected(group.fittingsDataSource, group.fittingsSelection)"
                                    [aria-label]="setCheckboxLabel(null, group.fittingsDataSource, group.fittingsSelection)"
                                    color="primary"
                                    *ngIf="wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                  >
                                  </mat-checkbox>
                                </th>
                                <td
                                  mat-cell
                                  class="select-column"
                                  *matCellDef="let row"
                                >
                                  <!--Select Row Checkbox-->
                                  <mat-checkbox
                                    (click)="$event.stopPropagation()"
                                    (change)="group.fittingsSelection.toggle(row); changeDetector.detectChanges()"
                                    disableRipple="true"
                                    [checked]="group.fittingsSelection.isSelected(row)"
                                    [aria-label]="setCheckboxLabel(row, group.fittingsDataSource, group.fittingsSelection)"
                                    color="primary"
                                    *ngIf="wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                  >
                                  </mat-checkbox>
                                </td>
                              </ng-container>

                              <!--Label-->
                              <ng-container matColumnDef="label">
                                <th
                                  class="label-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.label.description'|translate}}"
                                >
                                  {{'order.items.label.title'|translate}}
                                </th>
                                <td
                                  class="label-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Label"
                                >
                                  <span
                                    class="text-primary"
                                    *ngIf="element['quantity']"
                                  >
                                    ({{element['quantity']}})
                                  </span>
                                  <strong> {{element['label']}} </strong>
                                  <div
                                    class="text-muted assembly"
                                    *ngIf="element['referenceNumber']"
                                  >
                                    Assembly #{{element['referenceNumber']}}
                                  </div>
                                </td>
                              </ng-container>

                              <!--Dimensions-->
                              <ng-container matColumnDef="dimensions">
                                <th
                                  class="dimensions"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.dimensions.description'|translate}}"
                                >
                                  {{'order.items.dimensions.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Dimensions"
                                >
                                  <div *ngIf="element.dimensions">
                                    <div
                                      *ngFor="let dimension of element.dimensions"
                                    >
                                      <div *ngIf="dimension.display==='1'">
                                        <span class="text-muted"
                                          >{{dimension.key}})</span
                                        >
                                        {{dimension.label}} {{dimension.value}}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </ng-container>

                              <!--Joints-->
                              <ng-container matColumnDef="joints">
                                <th
                                  class="joints"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.joints.description'|translate}}"
                                >
                                  {{'order.items.joints.title'|translate}}
                                </th>
                                <td
                                  class="joints"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Joints"
                                >
                                  <div *ngIf="element['joints']">
                                    <div
                                      *ngFor="let joint of element['joints']; let i = index"
                                    >
                                      <span *ngIf="joint.id" class="text-muted"
                                        >J{{i+1}})
                                      </span>
                                      <span *ngIf="joint.id"
                                        >{{joint.label}}</span
                                      >
                                    </div>
                                  </div>
                                  <div *ngIf="element['joints'].length === 0">
                                    <span class="text-muted">N/A</span>
                                  </div>
                                </td>
                              </ng-container>

                              <!--Notes-->
                              <ng-container matColumnDef="notes">
                                <th
                                  class=""
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.notes.description'|translate}}"
                                >
                                  {{'order.items.notes.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="{{'order.items.notes.title'|translate}}"
                                >
                                  <span
                                    class="text-info"
                                    title="{{element['notes']}}"
                                  >
                                    {{element['notes']}}
                                  </span>
                                </td>
                              </ng-container>

                              <!--Other-->
                              <ng-container matColumnDef="other">
                                <th
                                  class="other"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.other.description'|translate}}"
                                >
                                  {{'order.items.other.title'|translate}}
                                </th>
                                <td
                                  class="other"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Other"
                                >
                                  <!--Materials-->
                                  <div *ngIf="element.materials">
                                    <div
                                      *ngFor="let material of element.materials"
                                    >
                                      <div *ngIf="material.id">
                                        <span class="text-muted"
                                          >Material:</span
                                        >
                                        {{material.label}}
                                      </div>
                                      <div *ngIf="material.id">
                                        <span class="text-muted">Gauge:</span>
                                        {{material['calculations']['gauge']?.value}}
                                      </div>
                                    </div>
                                  </div>

                                  <!--Specifications-->
                                  <div *ngIf="element['specifications']">
                                    <div
                                      *ngFor="let specification of element['specifications']"
                                    >
                                      <div *ngIf="specification.id">
                                        <span class="text-muted"
                                          >Specification:</span
                                        >
                                        {{specification.label}}
                                      </div>
                                    </div>
                                  </div>

                                  <!--Insulations-->
                                  <div *ngIf="element['insulations']">
                                    <div
                                      *ngFor="let insulation of element['insulations']"
                                    >
                                      <div *ngIf="insulation.id">
                                        <span class="text-muted"
                                          >Insulation:</span
                                        >
                                        {{insulation.label}}
                                      </div>
                                    </div>
                                  </div>

                                  <!--Holes-->
                                  <div
                                    *ngIf="element['holes']"
                                    id="holes-button-container"
                                  >
                                    <bc-atoms-button
                                      title="{{'order.items.holes.description'|translate}}"
                                      label="Holes: {{element['holes'].length }}"
                                      display="block"
                                      size="small"
                                      type="link"
                                      (onButtonClicked)="openFittingHoleDetailsModal(element)"
                                      openFittingHoleDetailsModal
                                    >
                                    </bc-atoms-button>
                                  </div>

                                  <!--Tag-->
                                  <div *ngIf="element.tag">
                                    <span class="text-muted">Tag:</span>
                                    {{element.tag}}
                                  </div>

                                  <!--Notes-->
                                  <div *ngIf="element['notes']">
                                    <span class="text-muted">Note:</span>
                                    {{element['notes']}}
                                  </div>
                                </td>
                              </ng-container>

                              <!--Fittings: Item Shipping Status-->
                              <ng-container matColumnDef="status">
                                <th
                                  class="status-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.status.description'|translate}}"
                                >
                                  {{'order.items.status.title'|translate}}
                                </th>
                                <td
                                  class="status-column editable-cell"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Status"
                                >
                                  <bc-atoms-dropdown
                                    title="{{'order.items.status.description'|translate}}"
                                    display="block"
                                    labelKey="label"
                                    size="small"
                                    label="{{element.shipping.status.label}}"
                                    [requireSelection]="element.shipping.status.hasMatchingStatusOption"
                                    [loading]="element.shipping.status.isUpdatingOrder"
                                    [disabled]="element.shipping.status.isUpdatingOrder || !wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                    [options]="wdOrderStatusService.orderItemShippingStatusOptions | async"
                                    [(model)]="element.shipping.status"
                                    (modelChange)="updateOrderItemShippingStatus(orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].id, element.id, $event)"
                                  >
                                  </bc-atoms-dropdown>
                                </td>
                              </ng-container>
                            </bc-templates-table-container>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!--Products-->
                    <div *ngIf="group.productsDataSource.data.length > 0">
                      <div class="fg-header">
                        <!--Title-->
                        <bc-atoms-input-group
                          labelTitle="{{group.label}} {{'order.fulfillment.groups.products.title'|translate}}"
                          labelDescription="{{group.label}} {{'order.fulfillment.groups.products.description'|translate}}"
                        >
                        </bc-atoms-input-group>

                        <!--Buttons-->
                        <div class="header-buttons">
                          <!--Status Update-->
                          <div
                            class="d-inline-block"
                            *ngIf="group.productsSelection.selected.length > 0"
                          >
                            <bc-atoms-dropdown
                              labelKey="label"
                              size="small"
                              title="{{'order.fulfillment.groups.products.status.description'|translate}}"
                              label="{{'order.fulfillment.groups.products.status.default.title'|translate}}"
                              type="success"
                              [requireSelection]="false"
                              [loading]="group.productsUpdating"
                              [disabled]="group.productsUpdating"
                              [options]="wdOrderStatusService.orderItemShippingStatusOptions | async"
                              [model]="group.productStatusesDropdownValue"
                              (modelChange)="updateSelectedOrderItemShippingStatuses(orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].id, group, $event, 'products')"
                            >
                            </bc-atoms-dropdown>
                          </div>
                        </div>
                      </div>
                      <div class="row">
                        <!--Products (non-fittings) Table-->
                        <div class="col-12 items-table">
                          <div class="no-more-tables">
                            <bc-templates-table-container
                              class="order-items-table"
                              [dataSource]="group.productsDataSource"
                              [headers]="orderProductColumns"
                              [showPagination]="false"
                              [isLoading]="isLoading"
                              [customColSpan]="orderProductColumns.length"
                              [paginationPageSize]="group.productsDataSource?.data?.length"
                              matSort
                            >
                              <!-- Checkbox Column -->
                              <ng-container matColumnDef="select">
                                <th
                                  mat-header-cell
                                  class="select-column"
                                  *matHeaderCellDef
                                >
                                  <!--Select All Checkbox-->
                                  <mat-checkbox
                                    (change)="$event ? masterToggle(group.productsDataSource, group.productsSelection) : null"
                                    disableRipple="true"
                                    [checked]="group.productsSelection.hasValue() &&
                                                                    isAllSelected(group.productsDataSource, group.productsSelection)"
                                    [indeterminate]="group.productsSelection.hasValue() &&
                                                                    !isAllSelected(group.productsDataSource, group.productsSelection)"
                                    [aria-label]="setCheckboxLabel(null, group.productsDataSource, group.productsSelection)"
                                    color="primary"
                                    *ngIf="wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                  >
                                  </mat-checkbox>
                                </th>
                                <td
                                  mat-cell
                                  class="select-column"
                                  *matCellDef="let row"
                                >
                                  <!--Select Row Checkbox-->
                                  <mat-checkbox
                                    (click)="$event.stopPropagation()"
                                    (change)="group.productsSelection.toggle(row); changeDetector.detectChanges()"
                                    disableRipple="true"
                                    [checked]="group.productsSelection.isSelected(row)"
                                    [aria-label]="setCheckboxLabel(row, group.productsDataSource, group.productsSelection)"
                                    color="primary"
                                    *ngIf="wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                  >
                                  </mat-checkbox>
                                </td>
                              </ng-container>

                              <!--Group-->
                              <ng-container matColumnDef="group">
                                <th
                                  class="group"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.group.description'|translate}}"
                                >
                                  {{'order.items.group.title'|translate}}
                                </th>
                                <td
                                  class="group"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Group"
                                >
                                  {{element['group'].label}}
                                  <div
                                    class="text-muted"
                                    *ngIf="element.referenceNumber"
                                  >
                                    Assembly #{{element['referenceNumber']}}
                                  </div>
                                </td>
                              </ng-container>

                              <!--Label-->
                              <ng-container matColumnDef="label">
                                <th
                                  class=""
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.label.description'|translate}}"
                                >
                                  {{'order.items.label.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Label"
                                >
                                  <span
                                    class="text-primary"
                                    *ngIf="element.quantity"
                                  >
                                    ({{element.quantity}})
                                  </span>
                                  <strong> {{element['label']}} </strong>
                                </td>
                              </ng-container>

                              <!--Attributes-->
                              <ng-container matColumnDef="attributes">
                                <th
                                  class="attributes"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.attributes.description'|translate}}"
                                >
                                  {{'order.items.attributes.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Attributes"
                                >
                                  <div *ngIf="element.attributes">
                                    <div
                                      *ngFor="let attribute of element.attributes"
                                    >
                                      <span class="text-muted"
                                        >{{attribute.label}}:</span
                                      >
                                      {{attribute.value}}
                                    </div>
                                  </div>
                                  <div *ngIf="!element.attributes">
                                    <span class="text-muted">N/A</span>
                                  </div>
                                </td>
                              </ng-container>

                              <!--Notes-->
                              <ng-container matColumnDef="notes">
                                <th
                                  class=""
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.notes.description'|translate}}"
                                >
                                  {{'order.items.notes.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="{{'order.items.notes.title'|translate}}"
                                >
                                  <span class="text-info">
                                    {{element.notes}}
                                  </span>
                                </td>
                              </ng-container>

                              <!--Other-->
                              <ng-container matColumnDef="other">
                                <th
                                  class="other"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.other.description'|translate}}"
                                >
                                  {{'order.items.other.title'|translate}}
                                </th>
                                <td
                                  class="other"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Other"
                                >
                                  <!--Materials-->
                                  <div *ngIf="element.materials">
                                    <div
                                      *ngFor="let material of element.materials"
                                    >
                                      <div *ngIf="material.id">
                                        <span class="text-muted"
                                          >Material:</span
                                        >
                                        {{material.label}}
                                      </div>
                                      <div *ngIf="material.id">
                                        <span class="text-muted">Gauge:</span>
                                        {{material.calculations['gauge']?.value}}
                                      </div>
                                    </div>
                                  </div>

                                  <!--Specifications-->
                                  <div *ngIf="element.specifications">
                                    <div
                                      *ngFor="let specification of element.specifications"
                                    >
                                      <div *ngIf="specification.id">
                                        <span class="text-muted"
                                          >Specification:</span
                                        >
                                        {{specification.label}}
                                      </div>
                                    </div>
                                  </div>

                                  <!--Tag-->
                                  <div *ngIf="element.tag">
                                    <span class="text-muted">Tag:</span>
                                    {{element.tag}}
                                  </div>

                                  <!--Notes-->
                                  <div *ngIf="element.notes">
                                    <span class="text-muted">Note:</span>
                                    {{element.notes}}
                                  </div>
                                </td>
                              </ng-container>

                              <!--Products (non-fitting): Item Shipping Status-->
                              <ng-container matColumnDef="status">
                                <th
                                  class="status-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.status.description'|translate}}"
                                >
                                  {{'order.items.status.title'|translate}}
                                </th>
                                <td
                                  class="status-column editable-cell"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Status"
                                >
                                  <bc-atoms-dropdown
                                    title="{{'order.items.status.description'|translate}}"
                                    display="block"
                                    labelKey="label"
                                    size="small"
                                    label="{{element.shipping.status.label}}"
                                    [requireSelection]="element.shipping.status.hasMatchingStatusOption"
                                    [loading]="element.shipping.status.isUpdatingOrder"
                                    [disabled]="element.shipping.status.isUpdatingOrder || !wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                                    [options]="wdOrderStatusService.orderItemShippingStatusOptions | async"
                                    [(model)]="element.shipping.status"
                                    (modelChange)="updateOrderItemShippingStatus(orderFormModel[wdLocalSharedFunctions.orderSectionType.DETAILS].id, element.id, $event)"
                                  >
                                  </bc-atoms-dropdown>
                                </td>
                              </ng-container>
                            </bc-templates-table-container>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!--Tracker Items-->
                    <div
                      *ngIf="group.trackerItemsDataSource.data.length > 0 && group.module?.id === 'tracker'"
                    >
                      <div class="fg-header">
                        <!--Title-->
                        <bc-atoms-input-group
                          labelTitle="{{'order.fulfillment.groups.tracker.title'|translate}}"
                          labelDescription="{{'order.fulfillment.groups.tracker.description'|translate}}"
                        >
                        </bc-atoms-input-group>
                      </div>
                      <div class="row">
                        <!--Tracker Items Table-->
                        <div class="col-12 items-table">
                          <div class="no-more-tables">
                            <bc-templates-table-container
                              class="order-items-table"
                              [dataSource]="group.trackerItemsDataSource"
                              [headers]="trackerItemColumns"
                              [showPagination]="false"
                              [isLoading]="isLoading"
                              [customColSpan]="trackerItemColumns.length"
                              [paginationPageSize]="group.trackerItemsDataSource?.data?.length"
                              matSort
                            >
                              <!--Label-->
                              <ng-container matColumnDef="label">
                                <th
                                  class=""
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.label.description'|translate}}"
                                >
                                  {{'order.items.label.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Label"
                                >
                                  <span
                                    class="text-primary"
                                    *ngIf="element.quantity"
                                  >
                                    ({{element.quantity}})
                                  </span>
                                  <strong> {{element['label']}} </strong>
                                </td>
                              </ng-container>

                              <!--Notes-->
                              <ng-container matColumnDef="notes">
                                <th
                                  class=""
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.notes.description'|translate}}"
                                >
                                  {{'order.items.notes.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="{{'order.items.notes.title'|translate}}"
                                >
                                  <span class="text-info">
                                    {{element.notes}}
                                  </span>
                                </td>
                              </ng-container>

                              <!--Tag-->
                              <ng-container matColumnDef="tag">
                                <th
                                  class="tag"
                                  mat-header-cell
                                  *matHeaderCellDef
                                  title="{{'order.items.tag.description'|translate}}"
                                >
                                  {{'order.items.tag.title'|translate}}
                                </th>
                                <td
                                  class="tag"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Tag"
                                >
                                  {{element.tag}}
                                </td>
                              </ng-container>
                            </bc-templates-table-container>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!--Adjustments-->
                    <div
                      *ngIf="group.adjustmentsDataSource.data.length > 0 &&
                        (wdEnvironmentService.permissions.canViewAdjustments || wdEnvironmentService.permissions.canManageAdjustments)"
                    >
                      <div class="fg-header">
                        <!--Title-->
                        <bc-atoms-input-group
                          labelIcon="adjustment"
                          labelTitle="{{group.label}} {{'order.fulfillment.groups.adjustments.title'|translate}}"
                          labelDescription="{{group.label}} {{'order.fulfillment.groups.adjustments.description'|translate}}"
                        >
                        </bc-atoms-input-group>
                      </div>
                      <div class="row">
                        <!--Adjustments Table-->
                        <div class="col-12 items-table">
                          <div class="no-more-tables">
                            <bc-templates-table-container
                              class="order-adjustments-table"
                              [dataSource]="group.adjustmentsDataSource"
                              [headers]="orderAdjustmentColumns"
                              [showPagination]="false"
                              [isLoading]="isLoading"
                              [customColSpan]="orderAdjustmentColumns.length"
                              [paginationPageSize]="group.adjustmentsDataSource?.data?.length"
                              matSort
                            >
                              <!-- Checkbox Column -->
                              <ng-container matColumnDef="select">
                                <th
                                  mat-header-cell
                                  class="select-column"
                                  *matHeaderCellDef
                                >
                                  <!--Select All Checkbox-->
                                  <mat-checkbox
                                    (change)="$event ? masterToggle(group.adjustmentsDataSource, group.productsSelection) : null"
                                    disableRipple="true"
                                    [checked]="group.productsSelection.hasValue() &&
                                                                isAllSelected(group.adjustmentsDataSource, group.productsSelection)"
                                    [indeterminate]="group.productsSelection.hasValue() &&
                                                                !isAllSelected(group.adjustmentsDataSource, group.productsSelection)"
                                    [aria-label]="setCheckboxLabel(null, group.adjustmentsDataSource, group.productsSelection)"
                                    color="primary"
                                  >
                                  </mat-checkbox>
                                </th>
                                <td
                                  mat-cell
                                  class="select-column"
                                  *matCellDef="let row"
                                >
                                  <!--Select Row Checkbox-->
                                  <mat-checkbox
                                    (click)="$event.stopPropagation()"
                                    (change)="group.productsSelection.toggle(row); changeDetector.detectChanges()"
                                    disableRipple="true"
                                    [checked]="group.productsSelection.isSelected(row)"
                                    [aria-label]="setCheckboxLabel(row, group.adjustmentsDataSource, group.productsSelection)"
                                    color="primary"
                                  >
                                  </mat-checkbox>
                                </td>
                              </ng-container>

                              <!--Label-->
                              <ng-container matColumnDef="label">
                                <th mat-header-cell *matHeaderCellDef>
                                  {{'order.fulfillment.groups.adjustments.fields.label.title'|translate}}
                                </th>
                                <td
                                  class=""
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Label"
                                  title="{{'order.fulfillment.groups.adjustments.fields.label.description'|translate}}"
                                >
                                  <span>{{element['label']}} </span>
                                </td>
                              </ng-container>

                              <!--Description-->
                              <ng-container matColumnDef="description">
                                <th mat-header-cell *matHeaderCellDef>
                                  {{'order.fulfillment.groups.adjustments.fields.description.title'|translate}}
                                </th>
                                <td
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Description"
                                  title="{{'order.fulfillment.groups.adjustments.fields.description.description'|translate}}"
                                >
                                  <span>{{element['description']}} </span>
                                </td>
                              </ng-container>

                              <!--Fabrication-->
                              <ng-container matColumnDef="fabrication">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.fabrication.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Fabrication"
                                  title="{{'order.fulfillment.groups.adjustments.fields.fabrication.description'|translate}}"
                                >
                                  <div *ngIf="element.shopLabour.value !== 0">
                                    <i
                                      class="fa"
                                      [ngClass]="{
                                                        'text-danger fa-arrow-down': element.shopLabour.value < 0,
                                                        'text-success fa-arrow-up': element.shopLabour.value > 0}"
                                    ></i>
                                    <strong
                                      [ngClass]="{
                                                        'text-danger': element.shopLabour.value < 0,
                                                        'text-success': element.shopLabour.value > 0}"
                                    >
                                      Fabrication</strong
                                    >
                                  </div>
                                  <div *ngIf="element.shopLabour.value !== 0">
                                    {{element['shopLabour']|BcxTime:true}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.shopLabour.value">-</div>
                                </td>
                              </ng-container>

                              <!--Weight-->
                              <ng-container matColumnDef="weight">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.weight.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Weight"
                                  title="{{'order.fulfillment.groups.adjustments.fields.weight.description'|translate}}"
                                >
                                  <div *ngIf="element.weight.value !== 0">
                                    <i
                                      class="fa"
                                      [ngClass]="{
                                                        'text-danger fa-arrow-down': element.weight.value < 0,
                                                        'text-success fa-arrow-up': element.weight.value > 0}"
                                    ></i>
                                    <strong
                                      [ngClass]="{
                                                        'text-danger': element.weight.value < 0,
                                                        'text-success': element.weight.value > 0}"
                                    >
                                      Weight</strong
                                    >
                                  </div>
                                  <div *ngIf="element.weight.value !== 0">
                                    {{element['weight']|BcxWeight}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.weight.value">-</div>
                                </td>
                              </ng-container>

                              <!--Cost-->
                              <ng-container matColumnDef="cost">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.cost.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Cost"
                                  title="{{'order.fulfillment.groups.adjustments.fields.cost.description'|translate}}"
                                >
                                  <div *ngIf="element.cost.value !== 0">
                                    <i
                                      class="fa"
                                      [ngClass]="{
                                                        'text-danger fa-arrow-down': element.cost.value < 0,
                                                        'text-success fa-arrow-up': element.cost.value > 0}"
                                    ></i>
                                    <strong
                                      [ngClass]="{
                                                        'text-danger': element.cost.value < 0,
                                                        'text-success': element.cost.value > 0}"
                                    >
                                      Cost</strong
                                    >
                                  </div>
                                  <div *ngIf="element.cost.value !== 0">
                                    {{element['cost']|BcxCurrency}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.cost.value">-</div>
                                </td>
                              </ng-container>

                              <!--Price-->
                              <ng-container matColumnDef="price">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.price.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Price"
                                  title="{{'order.fulfillment.groups.adjustments.fields.price.description'|translate}}"
                                >
                                  <div *ngIf="element.price.value !== 0">
                                    <i
                                      class="fa"
                                      [ngClass]="{
                                                        'text-danger fa-arrow-down': element.price.value < 0,
                                                        'text-success fa-arrow-up': element.price.value > 0}"
                                    ></i>
                                    <strong
                                      [ngClass]="{
                                                        'text-danger': element.price.value < 0,
                                                        'text-success': element.price.value > 0}"
                                    >
                                      Price</strong
                                    >
                                  </div>
                                  <div *ngIf="element.price.value !== 0">
                                    {{element['price']|BcxCurrency}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.price.value">-</div>
                                </td>
                              </ng-container>

                              <!--Material-->
                              <ng-container matColumnDef="material">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.material.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Material"
                                  title="{{'order.fulfillment.groups.adjustments.fields.material.description'|translate}}"
                                >
                                  <div *ngIf="element.material?.id">
                                    {{element.material.label}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.material?.id">-</div>
                                </td>
                              </ng-container>

                              <!--Gauge-->
                              <ng-container matColumnDef="gauge">
                                <th
                                  class="text-center value-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'order.fulfillment.groups.adjustments.fields.gauge.title'|translate}}
                                </th>
                                <td
                                  class="text-center value-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Gauge"
                                  title="{{'order.fulfillment.groups.adjustments.fields.gauge.description'|translate}}"
                                >
                                  <div *ngIf="element.material?.gauge?.value">
                                    {{element.material?.gauge.value}}
                                  </div>
                                  <!--No Value-->
                                  <div *ngIf="!element.material?.gauge?.value">
                                    -
                                  </div>
                                </td>
                              </ng-container>

                              <!--Delete-->
                              <ng-container matColumnDef="delete">
                                <th
                                  class="text-center button-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'actions.delete'|translate}}
                                </th>
                                <td
                                  class="text-center button-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Delete"
                                  title="{{'actions.delete'|translate}}"
                                >
                                  <div
                                    *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                                  >
                                    <bc-atoms-button
                                      [label]="('actions.delete'|translate)"
                                      [title]="('actions.delete'|translate)"
                                      [loading]="element.isLoading"
                                      [disabled]="element.isLoading"
                                      type="danger"
                                      icon="delete"
                                      display="block"
                                      size="small"
                                      [iconOnly]="true"
                                      [confirmTitle]="('actions.removeType'|translate:{
                                                    type:('order.details.attachments.title'|translate)
                                                })"
                                      (onButtonClicked)="deleteAdjustment(element)"
                                    >
                                      <div confirmation-body>
                                        <div>
                                          {{'prompts.confirmDeleteType'|translate:
                                          { type: element.label } }}
                                        </div>
                                      </div>
                                    </bc-atoms-button>
                                  </div>
                                </td>
                              </ng-container>

                              <!--Edit-->
                              <ng-container matColumnDef="edit">
                                <th
                                  class="text-center button-column"
                                  mat-header-cell
                                  *matHeaderCellDef
                                >
                                  {{'actions.edit'|translate}}
                                </th>
                                <td
                                  class="text-center button-column"
                                  mat-cell
                                  *matCellDef="let element"
                                  data-title="Edit"
                                  title="{{'actions.edit'|translate}}"
                                >
                                  <div
                                    *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                                  >
                                    <bc-atoms-button
                                      title="{{'actions.edit'|translate }}"
                                      icon="edit"
                                      display="block"
                                      size="small"
                                      [loading]="element.isLoading"
                                      [iconOnly]="true"
                                      (onButtonClicked)="manageAdjustment(group, element)"
                                    >
                                    </bc-atoms-button>
                                  </div>
                                </td>
                              </ng-container>
                            </bc-templates-table-container>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!--Shipping & Totals -->
        <div class="row">
          <!--Shipping Info-->
          <div
            *ngIf="showShippingDetailsContainer"
            class="col-12 col-md-12 col-lg-12 col-xl-6"
          >
            <div class="row">
              <!--Delivery-->
              <div
                class="delivery col-12 mb-4"
                *ngIf="orderFormModel.delivery.address"
                id="shipping-address"
              >
                <div class="card">
                  <div class="card-header">
                    <div class="header-title">
                      {{'order.delivery.description'|translate}}
                    </div>
                    <div
                      class="header-buttons"
                      *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                    >
                      <!--Edit-->
                      <div
                        class="d-inline-block"
                        *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing && orderFormModel.delivery.option.value === orderShippingOptions.ADDRESS"
                      >
                        <bc-atoms-button
                          title="{{'actions.edit'|translate }}"
                          icon="edit"
                          display="block"
                          size="small"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isLoading"
                          [iconOnly]="true"
                          (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                        >
                        </bc-atoms-button>
                      </div>

                      <!--Cancel-->
                      <div
                        class="d-inline-block"
                        *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                      >
                        <bc-atoms-button
                          title="{{'actions.cancel'|translate }}"
                          icon="cancel"
                          display="block"
                          size="small"
                          type="default"
                          [iconOnly]="true"
                          (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                        >
                        </bc-atoms-button>
                      </div>

                      <!--Save-->
                      <div
                        class="d-inline-block ml-2"
                        *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                      >
                        <bc-atoms-button
                          title="{{'actions.save'|translate }}"
                          icon="save"
                          display="block"
                          size="small"
                          type="success"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isChanges || shippingDetailsForm.form.invalid || !shippingDetailsForm.form.dirty"
                          [iconOnly]="true"
                          (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                        >
                        </bc-atoms-button>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <form #shippingDetailsForm="ngForm">
                      <div class="row">
                        <!--Company-->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="workOrders"
                            labelTitle="{{'order.delivery.company.title'|translate}}"
                            labelDescription="{{'order.delivery.company.description'|translate}}"
                          >
                            <bc-atoms-input
                              id="company"
                              name="company"
                              title="{{'order.delivery.company.description'|translate}}"
                              placeholder="{{'order.delivery.company.title'|translate}}"
                              size="small"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.company"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>

                        <!--Street -->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="shipped"
                            labelTitle="{{'order.delivery.street.title'|translate}}"
                            labelDescription="{{'order.delivery.street.description'|translate}}"
                            [required]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [state]="streetControl.status"
                            [stateInput]="streetControl.errors"
                            [stateTrigger]="streetControl.invalid && (streetControl.touched)"
                          >
                            <bc-atoms-input
                              id="street"
                              name="street"
                              title="{{'order.delivery.street.description'|translate}}"
                              placeholder="{{'order.delivery.street.title'|translate}}"
                              size="small"
                              [required]="true"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.street"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                              [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.street"
                              #streetControl="ngModel"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>

                        <!--City-->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="shipped"
                            labelTitle="{{'order.delivery.city.title'|translate}}"
                            labelDescription="{{'order.delivery.city.description'|translate}}"
                            [required]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [state]="cityControl.status"
                            [stateInput]="cityControl.errors"
                            [stateTrigger]="cityControl.invalid && (cityControl.touched)"
                          >
                            <bc-atoms-input
                              id="city"
                              name="city"
                              title="{{'order.delivery.city.description'|translate}}"
                              placeholder="{{'order.delivery.city.title'|translate}}"
                              size="small"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [required]="true"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.city"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                              [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.city"
                              #cityControl="ngModel"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>

                        <!-- Country -->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="shipped"
                            labelTitle="{{'order.delivery.country.title'|translate}}"
                            labelDescription="{{'order.delivery.country.description'|translate}}"
                            [required]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                          >
                            <bc-atoms-dropdown
                              id="country"
                              name="country"
                              title="{{'order.delivery.country.description'|translate}}"
                              placeholder="{{'order.delivery.country.title'|translate}}"
                              label="{{'order.delivery.country.title'|translate}}"
                              labelKey="label"
                              display="block"
                              size="small"
                              [requireSelection]="false"
                              [loading]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || (wdOrderShippingGeolocationsService.isLoading | async)"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || (wdOrderShippingGeolocationsService.isLoading | async)"
                              [options]="countryDropdownOptions"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.country"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING); updateStateOptions()"
                            >
                            </bc-atoms-dropdown>
                          </bc-atoms-input-group>
                        </div>

                        <!--State-->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="shipped"
                            labelTitle="{{'order.delivery.state.title'|translate}}"
                            labelDescription="{{'order.delivery.state.description'|translate}}"
                            [required]="orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing"
                            [state]="stateControl.status"
                            [stateInput]="stateControl.errors"
                            [stateTrigger]="stateControl.invalid && (stateControl.touched)"
                          >
                            <bc-atoms-typeahead
                              id="state"
                              name="state"
                              title="{{'order.delivery.state.description'|translate}}"
                              placeholder="{{'order.delivery.state.title'|translate}}"
                              labelKey="label"
                              size="small"
                              [required]="true"
                              [disabled]="!orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.country?.id || !orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving || (wdOrderShippingGeolocationsService.isLoading | async)"
                              [options]="stateDropdownOptions"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.state"
                              [(ngModel)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.state"
                              #stateControl="ngModel"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING, shippingDetailsForm.form)"
                            >
                            </bc-atoms-typeahead>
                          </bc-atoms-input-group>
                        </div>

                        <!-- Zip Code -->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="shipped"
                            labelTitle="{{'order.delivery.zipcode.title'|translate}}"
                            labelDescription="{{'order.delivery.zipcode.description'|translate}}"
                          >
                            <bc-atoms-input
                              id="zipCode"
                              name="zipCode"
                              title="{{'order.delivery.zipcode.description'|translate}}"
                              placeholder="{{'order.delivery.zipcode.title'|translate}}"
                              size="small"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.zipcode"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>

                        <!-- Phone Number -->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="user"
                            labelTitle="{{'order.delivery.phone.title'|translate}}"
                            labelDescription="{{'order.delivery.phone.description'|translate}}"
                          >
                            <bc-atoms-input
                              id="phone"
                              name="phone"
                              title="{{'order.delivery.phone.description'|translate}}"
                              placeholder="{{'order.delivery.phone.title'|translate}}"
                              size="small"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.phone"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>

                        <!-- Instructions -->
                        <div class="col-12 col-md-4 col-lg-6">
                          <bc-atoms-input-group
                            labelIcon="notifications"
                            labelTitle="{{'order.delivery.instructions.title'|translate}}"
                            labelDescription="{{'order.delivery.instructions.description'|translate}}"
                          >
                            <bc-atoms-input
                              id="instructions"
                              name="instructions"
                              title="{{'order.delivery.instructions.description'|translate}}"
                              placeholder="{{'order.delivery.instructions.title'|translate}}"
                              size="small"
                              [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING]?.isEditing || orderState[wdLocalSharedFunctions.orderSectionType.SHIPPING].isSaving"
                              [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.SHIPPING].address.instructions"
                              (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.SHIPPING)"
                            ></bc-atoms-input>
                          </bc-atoms-input-group>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!--Custom Additional Info-->
          <div
            *ngIf="wdEnvironmentService.constants.isCustomOrderFormFieldsEnabled && orderFormModel.formFields"
            class="col-12 col-md-12 col-lg-12 col-xl-6"
          >
            <div class="row">
              <div
                class="formFields col-12 mb-4"
                *ngIf="orderFormModel.formFields"
                id="formFields"
              >
                <div class="card">
                  <div class="card-header">
                    <div class="header-title">
                      <div class="header-title">
                        {{'order.details.sections.customForm'|translate}}
                      </div>
                    </div>
                    <div
                      class="header-buttons"
                      *ngIf="wdEnvironmentService.permissions.canEditOrderDetails"
                    >
                      <!--Edit-->
                      <div
                        class="d-inline-block"
                        *ngIf="!orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS]?.isEditing"
                      >
                        <bc-atoms-button
                          title="{{'actions.edit'|translate }}"
                          icon="edit"
                          display="block"
                          size="small"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS]?.isLoading"
                          [iconOnly]="true"
                          (onButtonClicked)="openEditSection(wdLocalSharedFunctions.orderSectionType.FORMFIELDS)"
                        >
                        </bc-atoms-button>
                      </div>

                      <!--Cancel-->
                      <div
                        class="d-inline-block"
                        *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS]?.isEditing"
                      >
                        <bc-atoms-button
                          title="{{'actions.cancel'|translate }}"
                          icon="cancel"
                          display="block"
                          size="small"
                          type="default"
                          [iconOnly]="true"
                          (onButtonClicked)="cancelEditSection(wdLocalSharedFunctions.orderSectionType.FORMFIELDS)"
                        >
                        </bc-atoms-button>
                      </div>

                      <!--Save-->
                      <div
                        class="d-inline-block ml-2"
                        *ngIf="orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS]?.isEditing"
                      >
                        <bc-atoms-button
                          title="{{'actions.save'|translate }}"
                          icon="save"
                          display="block"
                          size="small"
                          type="success"
                          [loading]="orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS].isSaving"
                          [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS].isChanges"
                          [iconOnly]="true"
                          (onButtonClicked)="saveEditSection(wdLocalSharedFunctions.orderSectionType.FORMFIELDS)"
                        >
                        </bc-atoms-button>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <form #formFieldsForm="ngForm">
                      <div class="row">
                        <div
                          class="col-12 col-md-12 col-lg-6"
                          *ngFor="let field of orderFormModel[wdLocalSharedFunctions.orderSectionType.FORMFIELDS]; index as i"
                        >
                          <bc-atoms-custom-input-field
                            [(model)]="orderFormModel[wdLocalSharedFunctions.orderSectionType.FORMFIELDS][i]"
                            [disabled]="!orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS].isEditing || orderState[wdLocalSharedFunctions.orderSectionType.FORMFIELDS].isSaving"
                            (modelChange)="checkForValueChanges(wdLocalSharedFunctions.orderSectionType.FORMFIELDS)"
                          ></bc-atoms-custom-input-field>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--Totals-->
          <div class="col-12 col-md-12 col-lg-12 col-xl-6">
            <div class="row">
              <!--Calculations-->
              <div
                class="calculations col-12 mb-4"
                *ngIf="orderFormModel.calculations"
              >
                <div
                  class="card"
                  *ngIf="wdEnvironmentService.permissions.canViewFabricationTime || wdEnvironmentService.permissions.canViewWeight
|| wdEnvironmentService.permissions.canViewPricing"
                >
                  <div class="card-header">
                    <div class="header-title">
                      {{'order.details.sections.totals'|translate}}
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <!--Fabrication Time-->
                      <div
                        class="col-12 col-md-4 col-lg-4"
                        *ngIf="wdEnvironmentService.permissions.canViewFabricationTime"
                      >
                        <bc-atoms-input-group
                          labelIcon="time"
                          labelTitle="{{'order.calculations.fabrication.title'|translate}}"
                          labelDescription="{{'order.calculations.fabrication.description'|translate}}"
                        >
                          <bc-atoms-input
                            id="orderFabrication"
                            name="orderFabrication"
                            title="{{'order.calculations.fabrication.description'|translate}}"
                            placeholder="{{'order.calculations.fabrication.title'|translate}}"
                            size="small"
                            [disabled]="true"
                            [(model)]="orderFormModel.calculations.fabrication.value"
                          >
                          </bc-atoms-input>
                        </bc-atoms-input-group>
                      </div>

                      <!--Weight-->
                      <div
                        class="col-xs-12 col-sm-4 col-md-4"
                        *ngIf="wdEnvironmentService.permissions.canViewWeight"
                        [ngClass]="{ 'col-sm-4 col-md-4': wdEnvironmentService.permissions.canViewFabricationTime,
                                              'col-sm-6 col-md-6': !wdEnvironmentService.permissions.canViewFabricationTime }"
                      >
                        <bc-atoms-input-group
                          labelIcon="weight"
                          labelTitle="{{'order.calculations.weight.title'|translate}}"
                          labelDescription="{{'order.calculations.weight.description'|translate}}"
                        >
                          <bc-atoms-input
                            id="orderWeight"
                            name="orderWeight"
                            title="{{'order.calculations.weight.description'|translate}}"
                            placeholder="{{'order.calculations.weight.title'|translate}}"
                            size="small"
                            [disabled]="true"
                            [(model)]="orderFormModel.calculations.weight.value"
                          >
                            <div suffix-content>
                              {{wdLocalSharedFunctions.translationKeys.weightUnit|translate}}
                            </div>
                          </bc-atoms-input>
                        </bc-atoms-input-group>
                      </div>

                      <!--Price-->
                      <div
                        class="col-xs-12"
                        *ngIf="wdEnvironmentService.permissions.canViewPricing"
                        [ngClass]="{ 'col-sm-4 col-md-4': wdEnvironmentService.permissions.canViewFabricationTime,
                                                 'col-sm-6 col-md-6': !wdEnvironmentService.permissions.canViewFabricationTime }"
                      >
                        <bc-atoms-input-group
                          labelIcon="reimbursements"
                          labelTitle="{{'order.calculations.price.title'|translate}}"
                          labelDescription="{{'order.calculations.price.description'|translate}}"
                        >
                          <bc-atoms-input
                            id="orderPrice"
                            name="orderPrice"
                            title="{{'order.calculations.price.description'|translate}}"
                            placeholder="{{'order.calculations.price.title'|translate}}"
                            size="small"
                            [disabled]="true"
                            [(model)]="orderFormModel.calculations.price.value"
                          >
                            <div prefix-content>
                              {{wdLocalSharedFunctions.translationKeys.currencySymbol|translate}}
                            </div>
                            <div suffix-content>
                              {{wdLocalSharedFunctions.units.currency}}
                            </div>
                          </bc-atoms-input>
                        </bc-atoms-input-group>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
